import { useEffect } from 'react';

import { useUser } from '@/features/auth/model/auth.selectors';
import { useAuthStore } from '@/features/auth/model/auth.store';
import { wsLogger } from '@/shared/lib/logger';

import { useConnectionStore } from '../stores/connection.store';
import {
  FOREGROUND_RECONCILE_THROTTLE_MS,
  markForegroundReconcileFired,
  markReconcileFetchFired,
  shouldRunForegroundReconcile,
  shouldRunReconcileFetch,
} from '../reconcile-throttle';
import { getAblyConnectionState } from '../utils';

interface UseTabVisibleReconcileOptions {
  isLeader: boolean;
  /**
   * Called when the tab becomes visible AND (for the leader) Ably is healthy AND both the
   * L3 (foreground, 30s) and L2 (inner, 10s) throttles release. Wired to
   * `handleReconnectSync` from the provider so it shares the same sync
   * bundle (queue + balance + profile + invalidations) as the L2 path.
   */
  onReconnect: () => void | Promise<void>;
}

/**
 * L3 reconcile (per WEB-L3 in docs/REALTIME_RECONCILE_PLAN.md).
 *
 * Covers the case where the tab was hidden BRIEFLY (< 30s grace, so the
 * existing visibility-handler in `useWebSocketConnection` did NOT disconnect
 * Ably) but client-side handlers may have been blocked or dropped events
 * during the hide — mutation lock active, throttled, BroadcastChannel
 * missed delivery, etc. When Ably stayed connected through the hide, the
 * existing visibility-handler does nothing; this hook fills that gap by
 * firing the same sync bundle on `visibilitychange → visible` + healthy
 * connection.
 *
 * If Ably dropped during the hide, the existing handler in
 * `useWebSocketConnection` calls `forceReconnect()` which cascades to the
 * L2 path on `(SUSPENDED|FAILED) → CONNECTED`. This hook short-circuits
 * in that case to avoid double-firing — the L2 inner throttle would
 * absorb it anyway, but skipping early keeps logs clean.
 *
 * Runs in every tab. Each tab keeps its own in-memory job queue and query
 * cache, and only raw WS frames cross tabs (BroadcastChannel), never the
 * result of a reconcile. A follower that missed a frame (it was hidden and
 * throttled, or the leader dropped its socket) has nothing else that heals
 * it on foreground. The fetch is a small REST bundle behind a 30s throttle.
 */
export function useTabVisibleReconcile({
  isLeader,
  onReconnect,
}: UseTabVisibleReconcileOptions) {
  const user = useUser();
  const token = useAuthStore((state) => state.token);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    if (!user?.id || !token) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        return;
      }

      // Leader with a bad socket: `useWebSocketConnection`'s handler owns it
      // (`forceReconnect()` cascades to L2). Skip here to avoid double-firing
      // the sync bundle. Followers hold no socket, so there is nothing to wait for.
      const ablyState = isLeader ? readAblyConnectionState() : 'connected';
      if (ablyState !== 'connected') {
        wsLogger.debug(
          `[L3_RECONCILE] reason=foreground skipped (ably=${ablyState ?? 'unknown'})`
        );
        return;
      }

      if (!shouldRunForegroundReconcile()) {
        wsLogger.debug(
          `[L3_RECONCILE] reason=foreground throttled outer (window=${FOREGROUND_RECONCILE_THROTTLE_MS}ms)`
        );
        return;
      }
      markForegroundReconcileFired();

      if (!shouldRunReconcileFetch()) {
        wsLogger.debug(
          '[L3_RECONCILE] reason=foreground throttled inner (L2 fired recently)'
        );
        return;
      }
      markReconcileFetchFired();
      wsLogger.info('[L3_RECONCILE] reason=foreground fired');

      const result = onReconnect();
      if (result instanceof Promise) {
        result.catch((error) => {
          wsLogger.error('[L3_RECONCILE] sync callback failed:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLeader, user?.id, token, onReconnect]);
}

function readAblyConnectionState(): string | null {
  // Shared reader (`utils.getAblyConnectionState`) — if Echo restructures
  // `connector.ably` this returns null and L3 silently no-ops, which is the
  // correct degradation.
  return getAblyConnectionState(useConnectionStore.getState().echo);
}
